/**
 * External Imports
 */
import { FC, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

/**
 * Internal Imports
 */
import Table from '../../../../shared/table';

import { toTitleCase } from '../../../../../utils/helpers';
import { setCensusReportSort } from '../../../../../redux/actions';

/**
 * Global Type Definition Imports
 */
import { CensusReport } from '../../../../../config/interfaces';

/**
 * Style Imports
 */
import {
  CellContainer,
  StyledLabel,
  StyledNeuTableRow
} from '../../../../shared/table/Table.styles';

interface IPatientCensusReportTableProps {
  censusList: CensusReport[];
  loading: boolean;
  totalTableCount: number;
}
const PatientCensusReportTable: FC<IPatientCensusReportTableProps> = ({
  censusList,
  loading,
  totalTableCount
}) => {
  const dispatch = useDispatch();
  const [sortKey, setSortKey] = useState<string>('date');
  const [sortDir, setSortDir] = useState<'desc' | 'asc'>('desc');
  const [active, setActive] = useState<string>('Census Date');

  const handleSort = (column: string) => {
    let key;
    switch (column) {
      case 'Census Date':
        key = 'date';
        break;
      case 'Unit':
        key = 'unit';
        break;
      case 'Room':
        key = 'room';
        break;
      case 'Patient & MRN':
        key = 'patient';
        break;
      case 'Date':
        key = 'lastRoundDate';
        break;
      case 'Last Rounded By':
        key = 'lastRoundedBy';
        break;
      case 'Status':
        key = 'status';
        break;
      default:
        key = '';
    }
    if (key === sortKey && sortDir === 'asc') {
      setSortDir('desc');
    } else {
      setSortDir('asc');
    }
    setActive(column);
    setSortKey(key);
  };

  useEffect(() => {
    dispatch(setCensusReportSort({ sortKey, sortDir }));
  }, [sortDir, sortKey]);

  const isTableEmpty = useMemo(() => censusList.length === 0, [censusList]);

  const headers: JSX.Element[] = [
    <CellContainer
      key="Census-Date"
      id="Patient-Census-Report-Table-Census-Date-Column"
      unit="column"
      colWidth={10}
      direction={!(active === 'Census Date') ? 'none' : sortDir}
      sort
    >
      Census Date
    </CellContainer>,
    <CellContainer
      key="Unit"
      id="Patient-Census-Report-Table-Unit-Column"
      unit="column"
      colWidth={18}
      direction={!(active === 'Unit') ? 'none' : sortDir}
      sort
    >
      Unit
    </CellContainer>,
    <CellContainer
      key="Room"
      id="Patient-Census-Report-Table-Room-Column"
      unit="column"
      colWidth={7}
      direction={!(active === 'Room') ? 'none' : sortDir}
    >
      Room
    </CellContainer>,
    <CellContainer
      key="Patient"
      id="Patient-Census-Report-Table-Patient-Column"
      unit="column"
      colWidth={20}
      direction={!(active === 'Patient & MRN') ? 'none' : sortDir}
    >
      Patient & MRN
    </CellContainer>,
    <CellContainer
      key="Date"
      id="Patient-Census-Report-Table-Date-Column"
      unit="column"
      colWidth={15}
      direction={!(active === 'Date') ? 'none' : sortDir}
    >
      Round Date
    </CellContainer>,
    <CellContainer
      key="Last Rounded By"
      id="Patient-Census-Report-Table-Last-Rounded-By-Column"
      unit="column"
      colWidth={20}
      direction={!(active === 'Last Rounded By') ? 'none' : sortDir}
    >
      Rounded By
    </CellContainer>,
    <CellContainer
      key="Status"
      id="Patient-Census-Report-Table-Status-Column"
      unit="column"
      colWidth={10}
      direction={!(active === 'Status') ? 'none' : sortDir}
      sort
    >
      Status
    </CellContainer>
  ];

  const rows = censusList?.map((item: CensusReport, i: number) => (
    <StyledNeuTableRow
      // eslint-disable-next-line react/no-array-index-key
      key={`Census-Report-Row-${i}`}
      id={`Patient-Census-Report-Row-${item.mrn}`}
      unit="row"
      size="default"
      height="5em"
    >
      <CellContainer unit="column" colWidth={10}>
        <StyledLabel>{item.censusDate}</StyledLabel>
      </CellContainer>
      <CellContainer unit="column" colWidth={18}>
        <StyledLabel style={{ whiteSpace: 'normal' }}>
          <div>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                color: 'black',
                fontWeight: 700
              }}
            >
              {item.unit}
            </div>
            <div> {item.unitId}</div>
          </div>
        </StyledLabel>
      </CellContainer>
      <CellContainer unit="column" colWidth={7}>
        <StyledLabel>{item.room}</StyledLabel>
      </CellContainer>
      <CellContainer unit="column" colWidth={20}>
        <StyledLabel style={{ whiteSpace: 'normal' }}>
          <div>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                color: 'black',
                fontWeight: 700
              }}
            >
              {toTitleCase(item.patient)}
            </div>
            <div> {item.mrn}</div>
          </div>
        </StyledLabel>
      </CellContainer>
      <CellContainer unit="column" colWidth={15}>
        <StyledLabel>{item.roundDate}</StyledLabel>
      </CellContainer>
      <CellContainer unit="column" colWidth={20}>
        <StyledLabel style={{ whiteSpace: 'normal' }}>
          <div>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                color: 'black',
                fontWeight: 700
              }}
            >
              {item.roundedBy}
            </div>
            <div> {item.lastRoundedById}</div>
          </div>
        </StyledLabel>
      </CellContainer>
      <CellContainer unit="column" colWidth={10}>
        <StyledLabel>{item.status}</StyledLabel>
      </CellContainer>
    </StyledNeuTableRow>
  ));

  return (
    <Table
      customClass="patient-census-report-table"
      headers={headers}
      rows={rows}
      isTableEmpty={isTableEmpty}
      isTableEmptySearch={false}
      loading={loading}
      type="patientCensusReport"
      totalResults={totalTableCount}
      onSort={(e: any) => handleSort(e.detail.label)}
    />
  );
};

export default PatientCensusReportTable;
